import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Clock, Phone } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: 'easeOut' },
};

const boutiques = [
  {
    key: 'martigny',
    image: 'https://www.lesnocesdeginkgo.ch/uploads/1/3/4/0/134055628/published/devant.jpeg',
    imageAlt: 'Boutique Martigny',
    address: 'Rue des Petits-Epineys 3, 1920 Martigny',
    phone: '027 722 27 50',
    since: '70+ ans',
    mapsUrl: 'https://maps.google.com/?q=Rue+des+Petits-Epineys+3+1920+Martigny',
  },
  {
    key: 'verbier',
    image: 'https://www.lesnocesdeginkgo.ch/uploads/1/3/4/0/134055628/vitrine_orig.jpeg',
    imageAlt: 'Boutique Verbier',
    address: 'Rte des Creux 6, 1936 Verbier',
    phone: '027 565 57 50',
    since: 'Depuis 2021',
    mapsUrl: 'https://maps.google.com/?q=Rte+des+Creux+6+1936+Verbier',
  },
  {
    key: 'sion',
    image: 'https://www.lesnocesdeginkgo.ch/uploads/1/3/4/0/134055628/v2_orig.jpeg',
    imageAlt: 'Boutique Sion',
    address: 'Av. du Ritz 33, 1950 Sion',
    phone: '027 323 23 16',
    since: 'Depuis 2026',
    mapsUrl: 'https://maps.google.com/?q=Av+du+Ritz+33+1950+Sion',
  },
];

const Boutique = () => {
  const { t } = useTranslation();

  useEffect(() => { document.title = `${t('boutique.title')} — Les Noces de Ginkgo`; }, [t]);

  return (
    <div className="font-sans bg-cream min-h-screen pt-16">

      {/* Header */}
      <section className="bg-ginkgo-900 py-20 md:py-28 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="text-gold text-xs uppercase tracking-[0.3em] font-medium mb-4">Martigny · Verbier · Sion</p>
          <h1 className="font-serif text-5xl md:text-6xl text-white font-medium">{t('boutique.title')}</h1>
          <p className="mt-3 text-white/60 font-serif italic text-xl">{t('boutique.subtitle')}</p>
        </motion.div>
      </section>

      {/* Intro */}
      <section className="bg-cream-dark py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-stone-600 text-base leading-relaxed">{t('boutique.description')}</p>
        </div>
      </section>

      {/* Boutiques alternating */}
      <section className="bg-cream py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 space-y-20 md:space-y-32">
          {boutiques.map((b, i) => (
            <motion.article
              key={b.key}
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.1 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? 'lg:[direction:rtl]' : ''}`}
              data-testid={`boutique-${b.key}`}
            >
              {/* Image */}
              <div className="overflow-hidden relative" style={{ direction: 'ltr' }}>
                <img
                  src={b.image}
                  alt={b.imageAlt}
                  className="w-full h-80 md:h-[450px] object-cover"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 bg-ginkgo-800 text-white text-xs px-3 py-1 uppercase tracking-wider">
                  {b.since}
                </div>
              </div>

              {/* Text */}
              <div style={{ direction: 'ltr' }}>
                <p className="text-xs uppercase tracking-[0.25em] text-gold font-medium mb-4">
                  {b.address}
                </p>
                <h2 className="font-serif text-4xl md:text-5xl text-ginkgo-800 font-medium mb-6">
                  {t(`boutique.${b.key}_title`)}
                </h2>
                <p className="text-stone-600 text-base leading-relaxed mb-8">
                  {t(`boutique.${b.key}_text`)}
                </p>

                <div className="space-y-3 mb-8">
                  <div className="flex items-center gap-3 text-stone-500 text-sm">
                    <MapPin size={15} className="text-ginkgo-600 flex-shrink-0" />
                    <a href={b.mapsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-ginkgo-800 transition-colors">
                      {b.address}
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-stone-500 text-sm">
                    <Phone size={15} className="text-ginkgo-600 flex-shrink-0" />
                    <a href={`tel:+41${b.phone.replace(/\s/g,'').substring(1)}`} className="hover:text-ginkgo-800 transition-colors">
                      {b.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-stone-500 text-sm">
                    <Clock size={15} className="text-ginkgo-600 flex-shrink-0" />
                    <Link to="/contact" className="hover:text-ginkgo-800 transition-colors">
                      {t('boutique.visit')}
                    </Link>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/commandes"
                    data-testid={`boutique-order-${b.key}`}
                    className="inline-flex items-center justify-center gap-2 bg-ginkgo-800 text-white px-6 py-3 text-xs uppercase tracking-widest font-medium hover:bg-ginkgo-900 transition-colors"
                  >
                    {t('boutique.order')}
                    <ArrowRight size={12} />
                  </Link>
                  <Link
                    to="/contact"
                    data-testid={`boutique-contact-${b.key}`}
                    className="inline-flex items-center justify-center gap-2 border border-ginkgo-800 text-ginkgo-800 px-6 py-3 text-xs uppercase tracking-widest font-medium hover:bg-ginkgo-50 transition-colors"
                  >
                    {t('boutique.visit')}
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Wedding services CTA */}
      <section className="bg-ginkgo-50 py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-3xl md:text-4xl text-ginkgo-800 font-medium mb-6">
              Un mariage à votre image
            </h2>
            <p className="text-stone-600 text-base leading-relaxed mb-8">
              Pour les Noces de Ginkgo, chaque mariage est une histoire unique. Du bouquet de la mariée à l'habillage complet de votre lieu de réception, nous vous accompagnons dans la création d'un univers floral et décoratif qui vous ressemble.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/galerie"
                data-testid="wedding-gallery-cta"
                className="inline-flex items-center justify-center gap-2 bg-ginkgo-800 text-white px-8 py-4 text-xs uppercase tracking-widest font-medium hover:bg-ginkgo-900 transition-colors"
              >
                Voir nos mariages
                <ArrowRight size={12} />
              </Link>
              <Link
                to="/commandes"
                data-testid="wedding-order-cta"
                className="inline-flex items-center justify-center gap-2 border border-ginkgo-800 text-ginkgo-800 px-8 py-4 text-xs uppercase tracking-widest font-medium hover:bg-ginkgo-50 transition-colors"
              >
                Demander un devis
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Boutique;
